import React, { useEffect, useRef, useState } from 'react';

import RecordIcon from '#/assets/mic.svg?react';
import StopIcon from '#/assets/mic_off.svg?react';
import sendIcon from '#/assets/sendw.svg';
import { useRecording } from '#/contexts/RecordingContext';
import { useAudioPlayback } from '#/contexts/PlaybackContext';
import { RecordingMode } from '#/types/preferences';
import { useTranslation } from 'react-i18next';
import styles from './audio-record.module.css';

interface AudioRecordProps {
  audioConfidence: number;
}

const AudioRecord: React.FC<AudioRecordProps> = ({
  audioConfidence,
}) => {
  const { recordingEnabled, isRecording, recordingPaused, startRecording, finishRecording, recordingMode } = useRecording();
  const { isPlaying, pausePlaying } = useAudioPlayback();
  const [ recordingStarted, setRecordingStarted ] = useState<boolean>(false);   // Has the recording been just started with button?
  const [ speechDetected, setSpeechDetected ] = useState<boolean>(false);   // Has speech been detected?
  const isAutomatic = recordingMode === RecordingMode.Automatic;
  const shouldShowVu = isAutomatic || isRecording;
  const currentlyRecording = (isRecording || recordingStarted) && !recordingPaused;
  // Remember last scales so we can smooth changes
  const lastScalesRef = useRef<number[]>(Array(10).fill(1));

  const { t } = useTranslation();
    const listeningText = currentlyRecording
    ? t('listening')
    : t('notListening');

  useEffect(() => {
    const bars = document.querySelectorAll(`.${styles.bar}`);
    if (recordingMode === RecordingMode.Automatic) {
      bars.forEach(b => (b as HTMLElement).style.setProperty('--scaleY', '1'));
    } else {
      bars.forEach(b => (b as HTMLElement).style.removeProperty('--scaleY'));
    }
  }, [recordingMode]);

  const BASE = 17;
  const MIN_PX = 7;
  const MAX_PX = 27;
  const MIN_SCALE = MIN_PX / BASE; // 4/14
  const MAX_SCALE = MAX_PX / BASE;
  const SPREAD = MAX_SCALE - MIN_SCALE;

  // helper
  const setAllBarsScale = (scale: number) => {
    const bars = document.querySelectorAll(`.${styles.bar}`);
    bars.forEach((bar, i) => {
      (bar as HTMLElement).style.setProperty('--scaleY', scale.toFixed(3));
      lastScalesRef.current[i] = scale;
      bar.classList.add(styles.active);
    });
  };

  // NEW: whenever recording flips off in automatic mode, clamp to min scale
  useEffect(() => {
    if (isAutomatic && !isRecording) {
      setAllBarsScale(MIN_SCALE);
    }
    if (isRecording) {
      // If recording has started, unset the flag
      setRecordingStarted(false);
    }
  }, [isAutomatic, isRecording]);

  function updateVUMeter(confidence: number) {
    const bars = document.querySelectorAll(`.${styles.bar}`);
    const level = Math.round(confidence * 10);

    if (confidence > 0.2) {
      // Change to send icon
      setSpeechDetected(true);
    }

    if (recordingMode === RecordingMode.Automatic) {
      if (!currentlyRecording) {
        // OFF: force all bars to 4px
        bars.forEach((bar, i) => {
          (bar as HTMLElement).style.setProperty('--scaleY', MIN_SCALE.toFixed(7));
          lastScalesRef.current[i] = MIN_SCALE;
          bar.classList.add(styles.active);
        });
        return;
      }

      // ON: target maps confidence 0..1 -> 4..24px
      const target = MIN_SCALE + confidence * SPREAD;

      // jitter gives variation; clamp keeps within [4px, 24px]
      const jitterFactor = 0.35 + 0.45 * confidence;
      const baseAlpha = 0.35; // smoothing

      bars.forEach((bar, i) => {
        const prev = lastScalesRef.current[i] ?? target;

        let scaleTarget = target + (Math.random() * 2 - 1) * jitterFactor * SPREAD;
        scaleTarget = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scaleTarget));

        // If we're at (or effectively at) max confidence, snap to EXACT max (24px) and skip smoothing
        const alpha = confidence >= 0.999 ? 1 : baseAlpha;
        if (confidence >= 0.999) scaleTarget = MAX_SCALE;

        const scale = prev + (scaleTarget - prev) * alpha;

        (bar as HTMLElement).style.setProperty('--scaleY', scale.toFixed(3));
        lastScalesRef.current[i] = scale;
        bar.classList.add(styles.active);
      });
    } else {
      // Non-automatic: your original red-bar threshold behavior
      bars.forEach((bar) => {
        const levelData = (bar as HTMLElement).dataset.level;
        const barLevel = levelData ? parseInt(levelData, 10) : 0;
        if (barLevel <= level) bar.classList.add(styles.active);
        else bar.classList.remove(styles.active);
        (bar as HTMLElement).style.removeProperty('--scaleY');
      });
    }
  }

  useEffect(() => {
    const bars = document.querySelectorAll(`.${styles.bar}`);
    if (recordingMode === RecordingMode.Automatic) {
      bars.forEach(b => ((b as HTMLElement).style.width = '7px')); // same initial size
    } else {
      bars.forEach(b => ((b as HTMLElement).style.width = ''));     // let CSS data-level widths work again
    }
  }, [recordingMode]);

  const record = () => {
    if (isPlaying) {
      pausePlaying();
    }

    setRecordingStarted(true);
    setSpeechDetected(false);
    startRecording();
  }

  const stop = () => {
    finishRecording();
  }

  const delayedStop = () => {
    setTimeout(() => { stop(); }, 500); // 500ms delay
  };

  const handlePointerStart = () => {
    // Push to talk is handled in MouseButtonDown/Up
    if (recordingMode === RecordingMode.PushToTalk) {
      record();
    }
    else {
      if (isRecording) {
        stop();
      }
      else {
        record();
      }
    }
  };

  const handlePointerEnd = () => {
    // Touch handler for mobile browsers
    if (recordingMode === RecordingMode.PushToTalk) {
      if (isRecording) {
        delayedStop();
      }
      else {
        stop();
      }
    }
  };

  useEffect(() => {
    updateVUMeter(audioConfidence);
  }, [audioConfidence]);

  return (
    recordingEnabled ? (
      <div className={styles.audioRecord} data-mode={isAutomatic ? 'automatic' : 'default'}>
        <div className={styles.topRow}>
          <div className={styles.microphoneTouchArea}
              onPointerDown={handlePointerStart}
              onPointerUp={handlePointerEnd}
              onPointerCancel={handlePointerEnd}
              onPointerLeave={handlePointerEnd}
              onContextMenu={(event) => event.preventDefault()}
            >
            <button
              type="button"
              className={`${styles.microphoneButton} ${currentlyRecording ? styles.pulse : ''}`}
              onContextMenu={(event) => event.preventDefault()}
            >
              {(() => {
                const iconClass = `${styles.microphoneIcon} ${currentlyRecording ? styles.iconOn : styles.iconOff}`;
                if (currentlyRecording && speechDetected && recordingMode != RecordingMode.Automatic) {
                  return (
                    <img
                      src={sendIcon}
                      className={iconClass}
                      alt="Microphone Icon"
                      draggable={false}
                    />
                  );
                } else if (currentlyRecording) {
                  return <RecordIcon className={iconClass} aria-label="Microphone Icon" />;
                } else {
                  return <StopIcon className={iconClass} aria-label="Microphone Icon" />;
                }
              })()}
            </button>
          </div>
          <div className={`${styles.vuMeter} ${shouldShowVu ? styles.visible : styles.hidden}`} data-mode={isAutomatic ? 'automatic' : 'default'}>
            <div className={styles.bar} data-level="1"></div>
            <div className={styles.bar} data-level="2"></div>
            <div className={styles.bar} data-level="3"></div>
            <div className={styles.bar} data-level="4"></div>
            <div className={styles.bar} data-level="5"></div>
            <div className={styles.bar} data-level="6"></div>
            <div className={styles.bar} data-level="7"></div>
            <div className={styles.bar} data-level="8"></div>
            <div className={styles.bar} data-level="9"></div>
            <div className={styles.bar} data-level="10"></div>
        </div>
      </div>
      {/* Listening / Not listening label */}
      {isAutomatic && (
        <div
          className={`${styles.listeningIndicator} ${currentlyRecording ? styles.on : styles.off}`}
          role="status"
          aria-live="polite"
        >
          {listeningText}
        </div>
      )}
      </div>
    ) : (
      <div className={styles.audioRecord}>
        <button
          type="button"
          className={`${styles.microphoneButton} ${styles.recordingDisabled}`}
        >
          <StopIcon
            className={`${styles.microphoneIcon} ${styles.iconOff}`}
            aria-label="Microphone Icon"
          />
        </button>
      </div>
    )
  );
};

export default AudioRecord;